import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl } from '../../config/api';
import { apiErrorMessage } from '../prescriptionForm';
import {
  QUESTIONS, SCALE, emptyCheckIn, isComplete, summarise, buildCheckInRequest,
} from '../readiness';
import '../../styles/ReadinessCard.css';

// How did you arrive today.
//
// # THE BUDGET IS EIGHT SECONDS
//
// This is answered standing in a gym, on a phone, before warming up. Four taps
// and a submit. Anything that takes longer does not get filled in, and a
// check-in nobody fills in is worse than none — the coach starts trusting a
// signal that has gone quiet.
//
// So: buttons, not sliders. A slider needs a drag and a look to confirm; a row
// of five needs one tap and shows its own answer.
//
// # WHY SKIPPING IS NOT NAGGED
//
// An athlete training three times a week has four days with nothing to say. The
// card offers itself and then gets out of the way; there is no badge, no streak
// and no reminder, because the honest days are the ones worth having.

const ReadinessCard = () => {
  const { token } = useAuth();

  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(emptyCheckIn);
  const [open, setOpen] = useState(false);
  // Until this is true we do not know whether today has an answer already, and
  // offering the form would let somebody open a BLANK one over a check-in they
  // had already made — then overwrite it by saving. Small race, real data loss.
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await axios.get(buildApiUrl('/readiness'), authConfig());
        // 204 is "not checked in", which is a normal day and not an error.
        if (live && response.status === 200 && response.data) {
          setSaved(response.data);
          setDraft({ ...response.data });
        }
      } catch {
        // A check-in that cannot be loaded must not take the training screen
        // down with it. The card simply offers a fresh one.
      } finally {
        if (live) setLoaded(true);
      }
    })();
    return () => { live = false; };
  }, [authConfig]);

  const submit = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await axios.put(
        buildApiUrl('/readiness'), buildCheckInRequest(draft), authConfig()
      );
      setSaved(data);
      setDraft({ ...data });
      setOpen(false);
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'Could not save your check-in.') });
    } finally {
      setBusy(false);
    }
  };

  const answered = isComplete(draft);

  if (!open) {
    return (
      <section className="card readiness-card readiness-collapsed">
        <div className="readiness-summary">
          <h2 className="section-title">How you arrived</h2>
          <p className="meta-copy">{loaded ? summarise(saved) : '…'}</p>
        </div>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setOpen(true)}
          disabled={!loaded}
        >
          {saved ? 'Change' : 'Check in'}
        </button>
      </section>
    );
  }

  return (
    <section className="card readiness-card">
      <h2 className="section-title">How you arrived</h2>
      <p className="meta-copy readiness-intro">
        Four taps. If you have a coach, they see how you arrived today.
      </p>

      {QUESTIONS.map((question) => (
        <fieldset className="readiness-question" key={question.id}>
          <legend className="readiness-legend">{question.label}</legend>
          <div className="readiness-scale" role="radiogroup" aria-label={question.label}>
            {SCALE.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={Number(draft[question.id]) === value}
                aria-label={`${question.label} ${value} of 5`}
                className={`readiness-option${Number(draft[question.id]) === value ? ' is-chosen' : ''}`}
                onClick={() => setDraft({ ...draft, [question.id]: value })}
              >
                {value}
              </button>
            ))}
          </div>
          {/* Both ends labelled, so the scale points somewhere without anybody
              having to guess what a 3 means. */}
          <div className="readiness-ends">
            <span>{question.low}</span>
            <span>{question.high}</span>
          </div>
        </fieldset>
      ))}

      <label className="readiness-notes">
        <span className="readiness-legend">Anything else</span>
        <input
          type="text"
          className="field"
          placeholder="Shoulder clicked on Friday"
          value={draft.notes ?? ''}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>

      {notice && <p className="status-error" role="alert">{notice.message}</p>}

      <div className="readiness-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={submit}
          disabled={!answered || busy}
        >
          {busy ? 'Saving…' : 'Save check-in'}
        </button>
        <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
          Not now
        </button>
      </div>
    </section>
  );
};

export default ReadinessCard;
